import { Stack, Typography, useTheme } from "@mui/material";
import type { Control } from "react-hook-form";
import {
	ClientPortfolioDomainEnum,
	ClientPortfolioPaymentFrequencyEnum,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import type {
	BuildingSavingsFormInput,
	BuildingSavingsFormOutput,
} from "web_myplann/Portfolio/Forms/BuildingSavingsForm/buildingSavingsFormSchema";
import { useProductCategoryOptions } from "web_myplann/Portfolio/Helpers/useProductCategoryOptions";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { FlatRowFormTextarea } from "web_myplann/Shared/Components/FlatRow/FlatRowFormTextarea";
import { useEnumSelectOptions } from "web_myplann/Shared/Hooks/useEnumSelectOptions";
import { useYesNoOptions } from "web_myplann/Shared/Hooks/useYesNoOptions";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type BuildingSavingsFormFieldsProps = {
	control: Control<
		BuildingSavingsFormInput,
		unknown,
		BuildingSavingsFormOutput
	>;
};

export const BuildingSavingsFormFields = ({
	control,
}: BuildingSavingsFormFieldsProps) => {
	const theme = useTheme();
	const { t } = useResource();

	const paymentFrequencyOptions = useEnumSelectOptions(
		ClientPortfolioPaymentFrequencyEnum,
		Resources.PortfolioDetail.SharedLabels.Frequency,
	);

	const stateContributionOptions = useYesNoOptions();

	const productCategoryOptions = useProductCategoryOptions(
		ClientPortfolioDomainEnum.BuildingSavings,
	);

	return (
		<>
			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.basicInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="name"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.name)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.fill)}
				/>
				<FlatRowFormInput
					control={control}
					name="contractNumber"
					label={t(
						Resources.Portfolio.AddBuildingSavings.Fields.contractNumber,
					)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="producer"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.producer)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormInput
					control={control}
					name="product"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.product)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
				/>
				<FlatRowFormSelect
					control={control}
					name="productCategoryId"
					label={t(
						Resources.Portfolio.AddBuildingSavings.Fields.productCategory,
					)}
					options={productCategoryOptions}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.values)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormInput
					control={control}
					name="payment"
					type="float"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.payment)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormSelect
					control={control}
					name="paymentFrequency"
					label={t(
						Resources.Portfolio.AddBuildingSavings.Fields.paymentFrequency,
					)}
					options={paymentFrequencyOptions}
				/>
				<FlatRowFormInput
					control={control}
					name="appreciation"
					type="float"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.appreciation)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix="%"
				/>
				<FlatRowFormInput
					control={control}
					name="targetAmount"
					type="float"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.targetAmount)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					suffix="Kč"
				/>
				<FlatRowFormSelect
					control={control}
					name="stateContribution"
					label={t(
						Resources.Portfolio.AddBuildingSavings.Fields.stateContribution,
					)}
					options={stateContributionOptions}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.additionalInfo)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormDatePicker
					control={control}
					name="signedAt"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.signedAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="startAt"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.startAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
				/>
				<FlatRowFormDatePicker
					control={control}
					name="endAt"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.endAt)}
					views={["year", "month", "day"]}
					displayFormat={DateFormat.Display}
					isLast
				/>
			</Stack>

			<Typography variant="portfolioSectionLabel">
				{t(Resources.Portfolio.AddBuildingSavings.Sections.note)}
			</Typography>
			<Stack bgcolor={theme.palette.background.paper}>
				<FlatRowFormTextarea
					control={control}
					name="note"
					label={t(Resources.Portfolio.AddBuildingSavings.Fields.note)}
					placeholder={t(Resources.Portfolio.AddShared.Placeholders.optional)}
					rows={1}
					isLast
				/>
			</Stack>
		</>
	);
};
